import React from 'react';

import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { StyledErrorText } from '@shared/components/errorText';
import { IconChevronDown } from '@shared/assets/images/svgs';
import CustomFormLabel from '@shared/components/forms/theme-elements/CustomFormLabel';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import { useTranslation } from 'react-i18next';

import {
  campaignWebhookEvents,
  CampaignWebhookAuthType,
  CampaignWebhookDraft,
  CampaignWebhookValidationErrors,
} from '@features/Admin/Campaign/utils/campaign-webhook-mapper';
import KeyValueHeadersEditor from '@features/Admin/PostCallIntegrations/ui/shared/KeyValueHeadersEditor';

interface CampaignWebhookFormProps {
  draft: CampaignWebhookDraft;
  errors: CampaignWebhookValidationErrors;
  disabled: boolean;
  isSaving: boolean;
  onChange: <K extends keyof CampaignWebhookDraft>(key: K, value: CampaignWebhookDraft[K]) => void;
  onAuthTypeChange: (authType: CampaignWebhookAuthType) => void;
  onAuthFieldChange: (field: keyof CampaignWebhookDraft['authConfig'], value: string) => void;
  onHeaderChange: (index: number, field: 'key' | 'value', value: string) => void;
  onAddHeader: () => void;
  onRemoveHeader: (index: number) => void;
}

const CampaignWebhookForm: React.FC<CampaignWebhookFormProps> = ({
  draft,
  errors,
  disabled,
  isSaving,
  onChange,
  onAuthTypeChange,
  onAuthFieldChange,
  onHeaderChange,
  onAddHeader,
  onRemoveHeader,
}) => {
  const { t } = useTranslation();

  const showStoredAuthHint =
    draft.authType !== 'none' &&
    draft.hasStoredAuth &&
    !draft.isAuthDirty &&
    draft.originalAuthType === draft.authType;

  return (
    <Box>
      <Stack spacing={0}>
        <Box>
          <CustomFormLabel htmlFor="webhook-name" sx={{ mb: 1, fontWeight: 500 }}>
            {t('campaignPage.webhooks.name')} *
          </CustomFormLabel>
          <CustomTextField
            id="webhook-name"
            fullWidth
            value={draft.name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChange('name', event.target.value)
            }
            placeholder={t('campaignPage.webhooks.placeholders.name')}
            disabled={disabled || isSaving}
            error={Boolean(errors.name)}
          />
          {errors.name ? <StyledErrorText>{t(errors.name)}</StyledErrorText> : null}
        </Box>

        <Stack gap={1} sx={{ mt: 2, mb: 2 }}>
          <TextField
            select
            label="Event enrollment"
            value={draft.events === null ? 'legacy' : 'explicit'}
            disabled={disabled || isSaving}
            onChange={(event) =>
              onChange('events', event.target.value === 'legacy' ? null : ['call.ended'])
            }>
            <MenuItem value="legacy">Legacy call ended</MenuItem>
            <MenuItem value="explicit">Selected lifecycle events</MenuItem>
          </TextField>
          <Typography variant="body2" color="text.secondary">
            Legacy retains existing call-ended timing and minimum-duration policy. Selected events
            are delivered automatically by their real lifecycle producers; manual dispatch applies
            only to legacy callbacks.
          </Typography>
          {draft.events !== null &&
            campaignWebhookEvents.map((event) => (
              <FormControlLabel
                key={event}
                label={event}
                control={
                  <Checkbox
                    checked={draft.events!.includes(event)}
                    disabled={disabled || isSaving}
                    onChange={(_, checked) =>
                      onChange(
                        'events',
                        checked
                          ? [...draft.events!, event]
                          : draft.events!.filter((value) => value !== event),
                      )
                    }
                  />
                }
              />
            ))}
          {draft.events !== null && (
            <Typography variant="body2" color="text.secondary">
              conversation.completed means the final customer and assistant text is durably saved
              after SDK session closure and the native call reaches a terminal state. It does not
              indicate business success.
            </Typography>
          )}
          {errors.events && <StyledErrorText>{errors.events}</StyledErrorText>}
          {draft.lastEventFailure && (
            <Alert severity="error">
              A {draft.lastEventFailure.event} event could not capture its delivery snapshot. No
              request was dispatched for that receipt. Contact an operator; editing current settings
              cannot replay the original event.
            </Alert>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <CustomFormLabel htmlFor="webhook-url" sx={{ mb: 1, fontWeight: 500 }}>
              {t('campaignPage.webhooks.url')} *
            </CustomFormLabel>
            <CustomTextField
              id="webhook-url"
              fullWidth
              value={draft.url}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onChange('url', event.target.value)
              }
              placeholder={t('campaignPage.webhooks.placeholders.url')}
              disabled={disabled || isSaving}
              error={Boolean(errors.url)}
            />
            {errors.url ? <StyledErrorText>{t(errors.url)}</StyledErrorText> : null}
          </Box>

          <Box sx={{ width: { xs: '100%', md: '220px' } }}>
            <CustomFormLabel htmlFor="webhook-method" sx={{ mb: 1, fontWeight: 500 }}>
              {t('campaignPage.webhooks.method')}
            </CustomFormLabel>
            <CustomSelect
              id="webhook-method"
              value={draft.httpMethod}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
                onChange('httpMethod', event.target.value as CampaignWebhookDraft['httpMethod'])
              }
              fullWidth
              disabled={disabled || isSaving}
              IconComponent={IconChevronDown}>
              <MenuItem value="POST">POST</MenuItem>
              <MenuItem value="GET">GET</MenuItem>
            </CustomSelect>
          </Box>
        </Stack>

        <Box sx={{ width: { xs: '100%', md: '220px' } }}>
          <CustomFormLabel htmlFor="webhook-auth-type" sx={{ mb: 1, fontWeight: 500 }}>
            {t('campaignPage.webhooks.authType')}
          </CustomFormLabel>
          <CustomSelect
            id="webhook-auth-type"
            value={draft.authType}
            onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
              onAuthTypeChange(event.target.value as CampaignWebhookAuthType)
            }
            fullWidth
            disabled={disabled || isSaving}
            IconComponent={IconChevronDown}>
            <MenuItem value="none">{t('campaignPage.webhooks.authTypes.none')}</MenuItem>
            <MenuItem value="basic">{t('campaignPage.webhooks.authTypes.basic')}</MenuItem>
            <MenuItem value="bearer">{t('campaignPage.webhooks.authTypes.bearer')}</MenuItem>
          </CustomSelect>
        </Box>

        {draft.authType !== 'none' ? (
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {showStoredAuthHint ? (
              <Alert severity="info" sx={{ alignItems: 'center' }}>
                {t('campaignPage.webhooks.storedAuthHint')}
              </Alert>
            ) : null}

            {draft.authType === 'basic' ? (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <CustomFormLabel sx={{ mb: 1, fontWeight: 500 }}>
                    {t('campaignPage.webhooks.username')}
                  </CustomFormLabel>
                  <CustomTextField
                    fullWidth
                    value={draft.authConfig.username}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      onAuthFieldChange('username', event.target.value)
                    }
                    placeholder={t('campaignPage.webhooks.placeholders.username')}
                    disabled={disabled || isSaving}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <CustomFormLabel sx={{ mb: 1, fontWeight: 500 }}>
                    {t('campaignPage.webhooks.password')}
                  </CustomFormLabel>
                  <CustomTextField
                    fullWidth
                    type="password"
                    value={draft.authConfig.password}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      onAuthFieldChange('password', event.target.value)
                    }
                    placeholder={t('campaignPage.webhooks.placeholders.password')}
                    disabled={disabled || isSaving}
                  />
                </Box>
              </Stack>
            ) : (
              <Box>
                <CustomFormLabel sx={{ mb: 1, fontWeight: 500 }}>
                  {t('campaignPage.webhooks.token')}
                </CustomFormLabel>
                <CustomTextField
                  fullWidth
                  type="password"
                  value={draft.authConfig.token}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onAuthFieldChange('token', event.target.value)
                  }
                  placeholder={t('campaignPage.webhooks.placeholders.token')}
                  disabled={disabled || isSaving}
                />
              </Box>
            )}

            {errors.auth ? <StyledErrorText>{t(errors.auth)}</StyledErrorText> : null}
          </Stack>
        ) : null}

        <Box sx={{ mt: 2 }}>
          <KeyValueHeadersEditor
            label={`${t('campaignPage.webhooks.headers')} (${t('common.optional')})`}
            addLabel={t('campaignPage.webhooks.addHeader')}
            keyLabel={t('campaignPage.webhooks.headerKey')}
            valueLabel={t('campaignPage.webhooks.headerValue')}
            emptyLabel={t('campaignPage.webhooks.noHeaders')}
            keyPlaceholder={t('campaignPage.webhooks.keyPlaceholder')}
            valuePlaceholder={t('campaignPage.webhooks.valuePlaceholder')}
            items={draft.headers}
            error={errors.headers ? t(errors.headers) : undefined}
            disabled={disabled || isSaving}
            onAdd={onAddHeader}
            onRemove={onRemoveHeader}
            onChange={onHeaderChange}
          />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          {t('campaignPage.webhooks.bodyTemplateDetailsHint')}
        </Typography>
      </Stack>
    </Box>
  );
};

export default CampaignWebhookForm;
